import {
  Box,
  Button,
  Container,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchItemsWithFilter } from '../api/generic';
import AddCustomerModal from '../components/AddCustomerModal';
import { Space } from '../components/Flex';
import PeriodFilterControls, { ViewMode } from '../components/PeriodFilterControls';
import { useAuthContext } from '../contexts/AuthContext';
import { Customer, SalesInvoice } from '../models/Sales';
import { prefixCollection } from '../utils/prefix';
import { toDate } from '../utils/timestamp';

const SalesInvoices = () => {
  const navigate = useNavigate();
  const { tenant, currency } = useAuthContext();
  const [salesInvoices, setSalesInvoices] = useState<SalesInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [filteringText, setFilteringText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [activeDate, setActiveDate] = useState<Date>(new Date());

  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const pageSize = 10;

  const handlePeriodChange = (dir: 'prev' | 'next') => {
    const newDate = new Date(activeDate);

    if (viewMode === 'month') {
      newDate.setMonth(activeDate.getMonth() + (dir === 'next' ? 1 : -1));
    } else if (viewMode === 'quarter') {
      newDate.setMonth(activeDate.getMonth() + (dir === 'next' ? 3 : -3));
    } else if (viewMode === 'year') {
      newDate.setFullYear(activeDate.getFullYear() + (dir === 'next' ? 1 : -1));
    }

    setActiveDate(newDate);
  };

  const getCustomerName = (customerId: string): string => {
    return customers.find((c) => c.id === customerId)?.name ?? 'Unknown';
  };

  const filteredItems = useMemo(() => {
    const start = new Date(activeDate);
    start.setDate(1);

    const end = new Date(start);

    if (viewMode === 'month') {
      end.setMonth(start.getMonth() + 1);
    } else if (viewMode === 'quarter') {
      end.setMonth(start.getMonth() + 3);
    } else if (viewMode === 'year') {
      end.setFullYear(start.getFullYear() + 1);
    }

    return salesInvoices.filter((invoice) => {
      const createdAt = toDate(invoice.createdAt);
      return (
        createdAt >= start &&
        createdAt < end &&
        (getCustomerName(invoice.customerId).toLowerCase().includes(filteringText.toLowerCase()) ||
          invoice.id.toLowerCase().includes(filteringText.toLowerCase()))
      );
    });
  }, [salesInvoices, viewMode, activeDate, filteringText, customers]);

  const paginatedItems = filteredItems.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize);

  const fetchSalesInvoicesAndCustomers = async () => {
    setLoading(true);
    const invoices = await fetchItemsWithFilter<SalesInvoice>(prefixCollection('salesInvoices', tenant), []);
    const customersFetched = await fetchItemsWithFilter<Customer>(prefixCollection('customers', tenant), []);
    setSalesInvoices(invoices);
    setCustomers(customersFetched);
    setLoading(false);
  };

  useEffect(() => {
    fetchSalesInvoicesAndCustomers();
  }, [tenant]);

  return (
    <Container
      header={
        <Header
          variant="h1"
          description="Sales Invoices linked to AGP bottles"
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="primary" onClick={() => navigate('/sales/new')}>
                Create Sales Invoice
              </Button>

              <Button onClick={() => setShowAddCustomerModal(true)}>Add Customer</Button>
            </SpaceBetween>
          }
        >
          Sales
        </Header>
      }
    >
      <PeriodFilterControls
        viewMode={viewMode}
        setViewMode={setViewMode}
        activeDate={activeDate}
        handlePeriodChange={handlePeriodChange}
        loading={loading}
      />
      <Space h={10} />
      <Table<SalesInvoice>
        columnDefinitions={[
          {
            id: 'id',
            header: 'Invoice ID',
            cell: (item) => (
              <Box variant="awsui-key-label">
                <Link variant="primary" href={`/sales/${item.id}`}>
                  {item.invoiceNumber}
                </Link>
              </Box>
            ),
          },
          {
            id: 'customer',
            header: 'Customer',
            cell: (item) => getCustomerName(item.customerId),
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => item.status,
          },
          {
            id: 'total',
            header: 'Total',
            cell: (item) => {
              const calcTotal = item.items?.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0) ?? 0;
              return `${currency(calcTotal.toFixed(2))}`;
            },
          },
        ]}
        items={paginatedItems}
        loading={loading}
        loadingText="Loading sales invoices..."
        empty="No sales invoices yet."
        filter={
          <TextFilter
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            filteringAriaLabel="Search invoices"
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            pagesCount={Math.ceil(filteredItems.length / pageSize)}
          />
        }
      />
      <AddCustomerModal
        visible={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCustomerAdded={() => {
          // Refetch customers if needed
          fetchSalesInvoicesAndCustomers();
        }}
      />
    </Container>
  );
};

export default SalesInvoices;
